import { CdkDrag, CdkDragDrop, CdkDragPreview, CdkDropList } from '@angular/cdk/drag-drop';
import { NgClass } from '@angular/common';
import type {
  AfterViewInit,
  EventEmitter,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import type { Sort } from '@angular/material/sort';
import { MatSort, MatSortModule } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
  MatTableModule,
} from '@angular/material/table';
import { environment } from '@env';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subscription, tap } from 'rxjs';

import {
  type ApplicationType as Application,
  ENVIRONMENT,
  FILE_DOCUMENT_TYPE,
  type FileIcon,
  FileService,
  OrganizationsService,
  permissions,
  UserService,
} from '@evc/platform';
import { FileSizePipe, isPreviewable, PermissionPipe } from '@evc/platform';
import {
  AnchoredPositionDirective,
  ApplicationIcon,
  BtnActionEnum,
  ButtonComponent,
  ConfirmModalComponent,
  debounce,
  IconComponent,
  InputComponent,
  type InputValueTypes,
  type Menu,
  MenuComponent,
  type MenuItemType,
  ModalService,
  StatusIndicatorComponent,
} from '@evc/web-components';
import { DateFormatPipe } from '@shared/pipes/date-format/date-format.pipe';
import { I18nService, TranslatePipe } from '@shared/services/i18n/i18n.service';

import { ActionsBarComponent } from '@app/components/actions-bar/actions-bar.component';
import { FileItemComponent } from '@app/components/file-item/file-item.component';
import { NoItemComponent } from '@app/components/no-item/no-item.component';
import { ApplicationsService } from '@app/services';
import { ToastActions } from '@app/store/actions';
import { FileItem } from '@app/types/file-item.type';
import { CtrlClickDirective } from '@app/utils/directives/ctrl-click/ctrl-click.directive';
import { FileDropDirective } from '@app/utils/directives/file-drop/file-drop.directive';

/** Anchor accepted by `AnchoredPositionDirective`: a real element or a floating-ui virtual element. */
type VirtualAnchor =
  HTMLElement | { getBoundingClientRect: () => DOMRect; contextElement?: HTMLElement };

@Component({
  selector: 'file-list',
  templateUrl: './file-list.component.html',
  styleUrls: ['./file-list.component.scss'],
  imports: [
    MatProgressBarModule,
    MatTableModule,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatSortModule,
    MatMenuModule,
    MenuComponent,
    AnchoredPositionDirective,
    InputComponent,
    IconComponent,
    ButtonComponent,
    ActionsBarComponent,
    FileItemComponent,
    NoItemComponent,
    CtrlClickDirective,
    FileDropDirective,
    NgClass,
    FileSizePipe,
    DateFormatPipe,
    TranslatePipe,
    PermissionPipe,
    StatusIndicatorComponent,
    CdkDrag,
    CdkDragPreview,
    CdkDropList,
  ],
  host: {
    '(document:keydown)': 'onKeydownHandler($event)',
    '(document:click)': 'onClickHandler($event)',
  },
})
export class FileListComponent implements AfterViewInit, OnChanges, OnInit, OnDestroy {
  readonly sortChanged = output<Sort>();
  readonly folderNavigate = output<FileItem>();
  readonly itemsSelected = output<FileItem[]>();
  readonly itemsToClipboard = output<{ items: FileItem[]; action: string }>();
  readonly itemsPasted = output<{ items: FileItem[]; parentId: string }>();
  readonly scrolledToBottom = output<number>();
  // Simple broadcast
  readonly createFolder = output();
  readonly startDefectAnalysis = output<FileItem>();
  readonly startAnomalyAnalysis = output<FileItem>();
  readonly renameItem = output<FileItem>();
  readonly copyItemsTo = output<FileItem[]>();
  readonly moveItemsTo = output<FileItem[]>();
  readonly itemDroppedOnFolder = output<{ items: FileItem[]; parentId: string }>();
  readonly downloadItems = output<FileItem[]>();
  readonly restoreItems = output<FileItem[]>();
  readonly deleteItems = output<FileItem[]>();
  readonly deleteItemsPermanently = output<FileItem[]>();
  readonly lockItems = output<FileItem[]>();
  readonly unlockItems = output<FileItem[]>();
  readonly fileOpened = output<{ file: FileItem; app: Application }>();
  readonly previewFile = output<FileItem>();
  readonly openWith = output<FileItem>();
  readonly filesUpload = output<{ files: File[]; paths?: string[] }>();

  broadcast<T>(action: string, payload?: T): void {
    const emitter = this[action] as EventEmitter<T>;
    if (!emitter) throw new Error(`Unknown broadcast key: ${action}`);

    emitter.emit(payload);
  }

  readonly loading = input.required<boolean>();
  readonly currentPage = input.required<number>();
  readonly totalPages = input<number>();
  readonly currentFolder = input<FileItem>();
  readonly lastAction = input<string>();

  isTrash = input(false);
  readonly itemsClipboard = input<FileItem[]>([]);
  readonly hasSearch = input(false);
  readonly isListEmpty = input(true);
  readonly areDeletedItems = input(false);
  readonly defaultSelected = input<FileItem[]>([]);

  readonly sort = viewChild.required(MatSort);

  displayedColumns: string[] = ['select', 'name', 'updatedAt', 'size', 'action'];

  // Context menu state
  protected contextMenuOpen = signal(false);
  protected contextMenuAnchor = signal<VirtualAnchor | undefined>(undefined);
  protected contextMenuConfig = computed(() => this.#buildMenuConfig());
  protected readonly isDragging = signal(false);
  protected readonly draggedItems = signal<FileItem[]>([]);
  dataSource!: MatTableDataSource<FileItem>;

  protected IsItemBeingDragged(row: FileItem): boolean {
    return this.isDragging() && this.draggedItems().some((item) => item.id === row.id);
  }

  protected PERMISSIONS = permissions;

  readonly #selectedSubject: BehaviorSubject<FileItem[]> = new BehaviorSubject<FileItem[]>([]);
  selected = toSignal(this.#selectedSubject, { initialValue: [] });

  #previousScroll = 0;
  #scrollDebounceTime = 0;
  readonly #subscription: Subscription = new Subscription();

  readonly #debouncedLoadNextPage = debounce(
    () => {
      const totalPages = this.totalPages() ?? 99999;
      if (this.currentPage() >= totalPages) return;
      this.scrolledToBottom.emit(this.currentPage() + 1);
      this.#scrollDebounceTime = 400;
    },
    () => this.#scrollDebounceTime,
  );

  @Input() set items(items: FileItem[]) {
    this.dataSource = new MatTableDataSource(items);
  }

  readonly #userService = inject(UserService);
  readonly #translateService = inject(TranslateService);
  readonly #applicationsService = inject(ApplicationsService);
  readonly #organizationService = inject(OrganizationsService);
  readonly #fileService = inject(FileService);

  onKeydownHandler(event: KeyboardEvent): void {
    if (!this.#userService.checkHasPermission(permissions.STORAGE_CREATE_UPDATE)) return;
    if (!(event.ctrlKey || event.metaKey)) return;

    const selectedItems = this.selected();

    const itemsClipboard = this.itemsClipboard();
    if (itemsClipboard?.length && event.key === 'v') {
      let parentFolderId: string = this.currentFolder()?.id || '';

      if (selectedItems.length === 1 && selectedItems[0].type === FILE_DOCUMENT_TYPE.FOLDER) {
        parentFolderId = selectedItems[0].id;

        if (itemsClipboard.some((i) => i.id === parentFolderId)) {
          return ToastActions.addToastWarning({ message: 'file.folder.pasteSelected' });
        }
      } else if (this.lastAction() === 'cut') {
        const isSameFolderPaste: boolean = itemsClipboard.some((i) => {
          const currentFolder = this.currentFolder();

          return (!i.parentId && !currentFolder) || i.parentId === (currentFolder?.id ?? null);
        });

        if (isSameFolderPaste) {
          return ToastActions.addToastWarning({ message: 'file.folder.pasteSame' });
        }
      }

      this.itemsPasted.emit({ items: itemsClipboard, parentId: parentFolderId });
    }

    if (!selectedItems.length) return;

    switch (event.key) {
      case 'c':
        this.itemsToClipboard.emit({ items: selectedItems, action: 'copy' });
        break;
      case 'x':
        this.itemsToClipboard.emit({ items: selectedItems, action: 'cut' });
        break;
      default:
        return;
    }
  }

  onClickHandler(event: MouseEvent): void {
    const selectedItems = this.selected();

    if (!selectedItems.length) return;

    const firstClass: string = event
      .composedPath()
      .map((x: HTMLElement) => x.className)
      .filter((c) => !!c)[0];

    const allowedClasses: string[] = [
      'file-list',
      'left-panel',
      'mat-drawer-content mat-sidenav-content container__wrapper',
    ];

    if (!allowedClasses.includes(firstClass)) return;
    this.#selectedSubject.next([]);
  }

  ngOnInit(): void {
    this.#subscription.add(
      this.#selectedSubject.subscribe((selected: FileItem[]) => {
        this.itemsSelected.emit(selected);
      }),
    );
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort();
  }

  ngOnChanges(changes: SimpleChanges): void {
    let selectedItems = this.selected();

    if (changes.hasSearch || changes.areDeletedItems) {
      const displayedColumns: string[] = ['select', 'name', 'updatedAt', 'size'];
      if (changes.hasSearch?.currentValue) displayedColumns.push('parent');
      if (changes.areDeletedItems?.currentValue) displayedColumns.push('parentName');
      this.displayedColumns = [...displayedColumns, 'action'];
    }

    if (changes.items) {
      const defaultSelected: FileItem[] =
        changes.defaultSelected?.currentValue || this.defaultSelected() || [];

      const allItems: Record<string, FileItem> = changes.items.currentValue.reduce(
        (all, currentValue) => ({ ...all, [currentValue.id]: currentValue }),
        {},
      );

      const allSelected: Record<string, FileItem> = [...selectedItems, ...defaultSelected].reduce(
        (all, currentValue) => ({ ...all, [currentValue.id]: currentValue }),
        {},
      );

      this.#selectedSubject.next(Object.values(allSelected).map((i) => allItems[i.id] ?? i));
    }

    if (changes.defaultSelected) {
      if (
        JSON.stringify(changes.defaultSelected.previousValue) !==
        JSON.stringify(changes.defaultSelected.currentValue)
      ) {
        this.#selectedSubject.next(changes.defaultSelected.currentValue);
        selectedItems = this.selected();
        if (selectedItems && selectedItems[0]) {
          const index: number = this.dataSource.data.findIndex(
            (item) => item.id === selectedItems[0].id,
          );
          setTimeout(() => {
            this.#goToItem(index);
          }, 200);
        }
      }
    }
  }

  isFolder(item: FileItem): boolean {
    return item.type === FILE_DOCUMENT_TYPE.FOLDER;
  }

  isFile(item: FileItem): boolean {
    return item.type === FILE_DOCUMENT_TYPE.FILE;
  }

  getFileIcon(item: FileItem): FileIcon {
    return this.#fileService.getFileIcon(item);
  }

  itemsForDrag(item: FileItem): FileItem[] {
    return this.isItemSelected(item) ? this.selected() : [item];
  }

  onDragStarted(item: FileItem): void {
    this.closeContextMenu();
    this.draggedItems.set(this.itemsForDrag(item));
    this.isDragging.set(true);
  }

  onDragEnded(): void {
    this.isDragging.set(false);
    this.draggedItems.set([]);
  }

  onItemDropped(event: CdkDragDrop<FileItem[]>): void {
    const items = event.item.data as FileItem[];
    if (
      !items.length ||
      items.some((item) => !item) ||
      this.isTrash() ||
      !this.#userService.checkHasPermission(permissions.STORAGE_CREATE_UPDATE)
    ) {
      return;
    }

    const targetRow = document
      .elementFromPoint(event.dropPoint.x, event.dropPoint.y)
      ?.closest<HTMLElement>('mat-row[data-folder-id]');
    const targetFolder = this.dataSource.data.find(
      (candidate) => candidate.id === targetRow?.dataset.folderId,
    );

    if (!targetFolder || !this.isFolder(targetFolder)) return;
    if (items.some((item) => item.id === targetFolder.id)) return;

    const movableItems = items.filter((item) => item.parentId !== targetFolder.id);
    if (!movableItems.length) return;

    this.itemDroppedOnFolder.emit({ items: movableItems, parentId: targetFolder.id });
  }

  hasProgress(item: FileItem): boolean {
    if (!item.progress) return false;

    return !isNaN(item.progress ?? 0);
  }

  onRowClick(item: FileItem, event: MouseEvent): void {
    this.closeContextMenu();

    if (event.ctrlKey || event.metaKey) {
      return;
    }

    this.#selectedSubject.next([item]);
  }

  onRowCtrlClick(item: FileItem): void {
    const selectedItems = this.selected();

    if (this.isItemSelected(item)) {
      this.#selectedSubject.next(selectedItems.filter((i) => i.id !== item.id));
    } else {
      this.#selectedSubject.next([...selectedItems, item]);
    }
  }

  onRowRightClick(item: FileItem, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const row = event.currentTarget as HTMLElement;
    const rowRect = row.getBoundingClientRect();
    const offsetX = event.clientX - rowRect.left;
    const offsetY = event.clientY - rowRect.top;

    this.#openContextMenu(item, {
      getBoundingClientRect: () => {
        const { left, top } = row.getBoundingClientRect();

        return new DOMRect(left + offsetX, top + offsetY);
      },
      contextElement: row,
    });
  }

  onMoreOptionsClick(item: FileItem, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const anchor = (event.currentTarget as HTMLElement) ?? (event.target as HTMLElement);
    this.#openContextMenu(item, anchor);
  }

  #openContextMenu(item: FileItem, anchor: VirtualAnchor): void {
    const targetSelectedItems = this.isItemSelected(item) ? this.selected() : [item];

    if (!this.isItemSelected(item)) {
      this.#selectedSubject.next(targetSelectedItems);
    }

    if (!this.#hasContextMenuActionsForItems(targetSelectedItems)) {
      this.closeContextMenu();
      return;
    }

    this.contextMenuAnchor.set(anchor);
    this.contextMenuOpen.set(true);
  }

  #modalService = inject(ModalService);
  #i18nService = inject(I18nService);
  #destroyRef = inject(DestroyRef);

  onRowDoubleClick(item: FileItem): void {
    if (this.isTrash()) {
      this.#modalService
        .open$<FileItem>(ConfirmModalComponent, {
          icon: 'trash',
          title: this.#i18nService.t('actions.restore.confirmModal.title'),
          content: this.#i18nService.t('actions.restore.confirmModal.content', { name: item.name }),
          action: this.#i18nService.t('actions.restore.action'),
          cancelLabel: this.#i18nService.t('actions.cancel'),
        })
        .pipe(
          tap(() => this.restoreItems.emit([item])),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }

    // Still allow navigation to folders on double-click
    if (item.type === FILE_DOCUMENT_TYPE.FOLDER) {
      return this.folderNavigate.emit(item);
    }

    this.previewFile.emit(item);
  }

  clearSelection(): void {
    this.#selectedSubject.next([]);
  }

  onSelectAll(): void {
    const selectedItems = this.selected();
    this.#selectedSubject.next(
      selectedItems.length === this.dataSource.data.length ? [] : [...this.dataSource.data],
    );
  }

  onRowSelect(item: FileItem, event: InputValueTypes): void {
    const selectedItems = this.selected();
    this.closeContextMenu();

    const shouldAddItem = Boolean(event);

    this.#selectedSubject.next(
      shouldAddItem ? [...selectedItems, item] : selectedItems.filter((i) => i.id !== item.id),
    );
  }

  onSortChanged(sortState: Sort): void {
    this.#selectedSubject.next([]);
    this.sortChanged.emit(sortState);
  }

  isItemSelected(item: FileItem): boolean {
    return !!this.selected().find((i) => i.id === item.id);
  }

  isAllItemsSelected(): boolean {
    return (
      this.dataSource.data.length > 0 && this.selected().length === this.dataSource.data.length
    );
  }

  isSomeItemsSelected(): boolean {
    return this.selected().length > 0 && !this.isAllItemsSelected();
  }

  areLockable(items: FileItem[]): boolean {
    return items.every((item) => !item.isLocked);
  }

  areUnlockable(items: FileItem[]): boolean {
    if (!this.#userService.checkHasPermission(permissions.STORAGE_CREATE_UPDATE)) return false;

    return items.every((item) => item.isLocked);
  }

  goToParent(item: FileItem): void {
    this.folderNavigate.emit({ id: item.parentId } as FileItem);
  }

  onScroll(e: Event | any): void {
    // Keep the menu open while scrolling; only close once its anchor is no longer visible in the table.
    if (this.contextMenuOpen() && !this.#isAnchorVisibleWithin(e.target as HTMLElement)) {
      this.closeContextMenu();
    }

    const tableViewHeight: number = e.target.offsetHeight; // viewport
    const tableScrollHeight: number = e.target.scrollHeight; // length of all table
    const scrollLocation: number = e.target.scrollTop; // how far user scrolled

    // If the user has scrolled within 100px of the bottom, add more data
    const buffer = 100;
    const limit: number = tableScrollHeight - tableViewHeight - buffer;

    if (this.#previousScroll < scrollLocation && scrollLocation > limit && !this.loading()) {
      this.#debouncedLoadNextPage();
    }

    this.#previousScroll = scrollLocation;
  }

  ngOnDestroy(): void {
    this.#subscription.unsubscribe();
  }

  closeContextMenu(): void {
    this.contextMenuOpen.set(false);
    this.contextMenuAnchor.set(undefined);
  }

  #isAnchorVisibleWithin(container: HTMLElement): boolean {
    const anchor = this.contextMenuAnchor();
    if (!anchor) return false;

    const anchorRect = anchor.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    return anchorRect.bottom > containerRect.top && anchorRect.top < containerRect.bottom;
  }

  protected hasContextMenuActionsForItem(item: FileItem): boolean {
    return this.#hasContextMenuActionsForItems([item]);
  }

  #hasContextMenuActionsForItems(selectedItems: FileItem[]): boolean {
    return (this.#buildMenuConfigForItems(selectedItems)?.items?.length ?? 0) > 0;
  }

  #buildMenuConfig(): Menu {
    return this.#buildMenuConfigForItems(this.selected());
  }

  #buildMenuConfigForItems(selectedItems: FileItem[]): Menu {
    const items: MenuItemType[] = [];
    const canReadStorage = this.#userService.checkHasPermission(permissions.STORAGE_READ);
    const canDownloadStorage = this.#userService.checkHasPermission(permissions.STORAGE_DOWNLOAD);
    const canCreateOrUpdateStorage = this.#userService.checkHasPermission(
      permissions.STORAGE_CREATE_UPDATE,
    );
    const canDeleteStorage = this.#userService.checkHasPermission(permissions.STORAGE_DELETE);
    const canCreateAiJob = this.#userService.checkHasPermission(permissions.STORAGE_AI_CREATE_JOB);

    if (!selectedItems.length || this.areDeletedItems()) {
      return this.#buildDeletedItemsMenu(selectedItems);
    }

    const openables = this.#applicationsService.openables();
    if (
      this.#applicationsService.isOneFileSelected(selectedItems) &&
      openables &&
      openables.length > 0
    ) {
      const selectedItem = selectedItems[0];

      if (selectedItem.latestVersion?.contentType === 'application/data-nde') {
        const isSingleApp = openables.length === 1;
        const openWithKey = isSingleApp ? 'list.menu.openWithApp' : 'list.menu.openWith';

        items.push({
          key: 'openWith',
          text: this.#translateService.stream(openWithKey, {
            appName: isSingleApp ? openables[0].name : undefined,
          }),
          icon: isSingleApp ? undefined : 'apps-open-with',
          app: isSingleApp ? (openables[0].name.toLowerCase() as ApplicationIcon) : undefined,
          action: {
            type: BtnActionEnum.CALLBACK,
            callbackOrUrl: () => this.openWith.emit(selectedItem),
          },
        });
      }
    }

    // Preview - single previewable file
    if (
      canReadStorage &&
      this.#applicationsService.isOneFileSelected(selectedItems) &&
      isPreviewable(selectedItems[0])
    ) {
      items.push({
        key: 'preview',
        text: this.#translateService.stream('list.menu.preview'),
        icon: 'external-context',
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.previewFile.emit(selectedItems[0]),
        },
      });
    }

    // Copy - only files
    if (canCreateOrUpdateStorage && this.#applicationsService.areOnlyFiles(selectedItems)) {
      items.push({
        key: 'copy',
        text: this.#translateService.stream('list.menu.copy'),
        icon: 'copy',
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.copyItemsTo.emit(selectedItems),
        },
      });
    }

    // Move To
    if (canCreateOrUpdateStorage) {
      items.push({
        key: 'moveTo',
        text: this.#translateService.stream('list.menu.moveTo'),
        icon: 'file-export',
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.moveItemsTo.emit(selectedItems),
        },
      });
    }

    // Rename - only one item
    if (canCreateOrUpdateStorage && this.#applicationsService.isOneSelected(selectedItems)) {
      items.push({
        key: 'rename',
        text: this.#translateService.stream('list.menu.rename'),
        icon: 'edit',
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.renameItem.emit(selectedItems[0]),
        },
      });
    }

    // Download - only files
    if (canDownloadStorage && this.#applicationsService.areOnlyFiles(selectedItems)) {
      items.push({
        key: 'download',
        text: this.#translateService.stream('list.menu.download'),
        icon: 'download',
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.downloadItems.emit(selectedItems),
        },
      });
    }

    const isProd = environment.ENV === ENVIRONMENT.production;
    const currentOrganizationHasAIFlows =
      (environment.AI_FLOWS_POC_ORG_IDS ?? []).indexOf(
        this.#organizationService.currentOrgId() ?? '',
      ) !== -1;

    if (
      canCreateAiJob &&
      this.#applicationsService.isOneFileSelected(selectedItems) &&
      (!isProd || currentOrganizationHasAIFlows)
    ) {
      const aiWorkflows = ['defectAnalysis', 'anomalyAnalysis'];

      items.push({
        key: 'aiWorkflows',
        text: this.#translateService.stream('list.menu.aiWorkflows'),
        icon: 'live',
        collapsible: aiWorkflows.map((workflow) => ({
          key: `ai-${workflow}`,
          text: this.#translateService.stream(`list.menu.${workflow}`),
          action: {
            type: BtnActionEnum.CALLBACK,
            callbackOrUrl: () => {
              if (workflow === 'defectAnalysis') {
                this.startDefectAnalysis.emit(selectedItems[0]);
              } else if (workflow === 'anomalyAnalysis') {
                this.startAnomalyAnalysis.emit(selectedItems[0]);
              }
            },
          },
        })),
      });
    }

    // Delete
    if (canDeleteStorage) {
      items.push({
        key: 'delete',
        text: this.#translateService.stream('list.menu.delete'),
        icon: 'trash-send',
        isWarn: true,
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.deleteItems.emit(selectedItems),
        },
      });
    }

    return { items };
  }

  #buildDeletedItemsMenu(selectedItems: FileItem[]): Menu {
    if (!selectedItems.length) {
      return { items: [] };
    }

    const items: MenuItemType[] = [];
    const canRestoreItems = this.#userService.checkHasPermission(permissions.STORAGE_CREATE_UPDATE);
    const canDeletePermanently = this.#userService.checkHasPermission(
      permissions.STORAGE_PERMANENT_DELETE,
    );

    if (canRestoreItems) {
      items.push({
        key: 'restore',
        text: this.#translateService.stream('list.menu.restore'),
        icon: 'undo',
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.restoreItems.emit(selectedItems),
        },
      });
    }

    if (canDeletePermanently) {
      items.push({
        key: 'deletePermanently',
        text: this.#translateService.stream('list.menu.permanentlyDelete'),
        icon: 'trash',
        isWarn: true,
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.deleteItemsPermanently.emit(selectedItems),
        },
      });
    }

    return { items };
  }

  /* istanbul ignore next */
  #goToItem(index: number): void {
    const wrapper = document.body.querySelector('.file-list') as HTMLElement | null;
    if (!wrapper) return;
    const mainTable = wrapper.querySelector('mat-table') as HTMLElement | null;
    if (!mainTable) return;

    const elements: NodeListOf<HTMLElement> = mainTable.querySelectorAll('mat-row');
    const element: HTMLElement = elements.item(index);

    const wrapperHeight: number = wrapper.clientHeight;
    const wrapperTop: number = wrapper.getBoundingClientRect().top;
    const elementTop: number = element.getBoundingClientRect().top;

    // element is in the visible part
    if (elementTop < wrapperTop + wrapperHeight) {
      return;
    }

    mainTable.scroll({
      top: elementTop + wrapperTop - wrapperHeight / 2,
      behavior: 'smooth',
    });
  }
}
